import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate, useRouteContext, useRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { updateOrganisation } from '@/api/resources/organisations'
import { EditOrganisationForm } from '@/components/organisations/edit-organisation-form'
import { Sheet, SheetContent } from '@/components/ui/sheet'
import { useRouteModal } from '@/hooks/use-route-modal'
import { useServerErrors } from '@/hooks/use-server-errors'
import { queryKeys } from '@/lib/query-keys'

export const Route = createFileRoute('/_app/organisations/$organisationId/edit')({
  head: () => ({ meta: [{ title: 'Edit Organisation - MyJunea' }] }),
  component: EditOrganisationRoute,
})

function EditOrganisationRoute(): React.ReactElement {
  const { organisationId } = Route.useParams()
  const { organisation } = useRouteContext({ from: '/_app/organisations/$organisationId' })
  const navigate = useNavigate()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { open, handleClose, handleOpenChange } = useRouteModal(
    () =>
      void navigate({
        to: '/organisations/$organisationId',
        params: { organisationId },
        resetScroll: false,
      }),
  )
  const { serverErrors, handleMutationError, clearServerErrors } = useServerErrors()

  const organisationIdNum = Number(organisationId)

  const mutation = useMutation({
    mutationFn: async (values: Parameters<typeof updateOrganisation>[1]) =>
      updateOrganisation(organisationIdNum, values),
    onSuccess: () => {
      clearServerErrors()
      void queryClient.invalidateQueries({
        queryKey: queryKeys.organisations.detail(organisationIdNum),
      })
      void queryClient.invalidateQueries({ queryKey: queryKeys.organisations.all })
      void router.invalidate()
      toast.success('Organisation updated')
      handleClose()
    },
    onError: handleMutationError,
  })

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent side="right" className="flex flex-col sm:max-w-lg">
        <EditOrganisationForm
          organisation={organisation}
          onSubmit={(values) => mutation.mutate(values)}
          isPending={mutation.isPending}
          serverErrors={serverErrors}
        />
      </SheetContent>
    </Sheet>
  )
}
